import React from 'react';
import { observer } from 'mobx-react-lite';
import { colors, InputText } from '@insurancemenu/imenu-components-lib';
import FormModal from '@insurancemenu/imenu-components-lib/dist/components/modals/FormModal';
import { Add, Delete, InfoButton, Mail } from '@insurancemenu/imenu-components-lib/dist/icons';
import { ManageContactsModalStore } from 'store/pages/rfq/initiate-rfq/pick-carriers/manageContactsModalStore';
import {
  CcFieldsListStyled,
  CcFieldWrapperStyled,
  PrimaryEmailSectionStyled,
  ModalContentStyled,
  ButtonStyled,
  ListTitleStyled,
  ErrorStyled,
} from './styles';
import {
  PICK_CARRIERS_CARRIER_CONTACTS_MODAL_ADD_CC_BTN_TEST_ID,
  PICK_CARRIERS_CARRIER_CONTACTS_MODAL_ERROR_TEST_ID,
  PICK_CARRIERS_CARRIER_CONTACTS_MODAL_REMOVE_CC_BTN_TEST_ID,
  PICK_CARRIERS_CARRIER_ROW_CONTACTS_MODAL_TEST_ID,
  PICK_CARRIERS_CONTACTS_MODAL_PRIMARY_EMAIL_TEST_ID,
} from '../../constants';

interface Props {
  store: ManageContactsModalStore;
}

const ManageContactsModal: React.FC<Props> = observer(({ store }) => {
  const {
    form,
    ccFields,
    showManageContactsModal,
    checkEmailsUniqueness,
    closeManageContactsModal,
    getCcField,
    removeCc,
    addCc,
    saveContacts,
  } = store;

  const areEmailsUnique = checkEmailsUniqueness();

  const FormContent = (
    <ModalContentStyled>
      <ListTitleStyled>Primary email:</ListTitleStyled>
      <PrimaryEmailSectionStyled>
        <InputText
          name="primaryEmail"
          value={form.fields.primaryEmail.value || ''}
          onChange={form.onFieldChange}
          width="30rem"
          validationPassed={!form.fields.primaryEmail.unpassedValidationMsg}
          validationMessage={form.fields.primaryEmail.unpassedValidationMsg || ''}
          data-test-id={PICK_CARRIERS_CONTACTS_MODAL_PRIMARY_EMAIL_TEST_ID}
        />
        <ButtonStyled
          type="text"
          color="primary"
          size="small"
          leftIconProp={<Add />}
          onClick={addCc}
          data-test-id={PICK_CARRIERS_CARRIER_CONTACTS_MODAL_ADD_CC_BTN_TEST_ID}
        >
          Add CC
        </ButtonStyled>
      </PrimaryEmailSectionStyled>
      {ccFields.length > 0 && (
        <>
          <ListTitleStyled>CC emails:</ListTitleStyled>
          <CcFieldsListStyled>
            {ccFields.map((fieldId) => (
              <CcFieldWrapperStyled key={fieldId}>
                <InputText
                  name={fieldId}
                  value={getCcField(fieldId).value || ''}
                  onChange={form.onFieldChange}
                  width="30rem"
                  validationPassed={!getCcField(fieldId).unpassedValidationMsg}
                  validationMessage={getCcField(fieldId).unpassedValidationMsg || ''}
                  data-test-id={`${fieldId}-modal-cc-input`}
                />
                <ButtonStyled
                  type="text"
                  color="custom"
                  size="small"
                  customColor={colors.red[600]}
                  onClick={() => removeCc(fieldId)}
                  leftIconProp={<Delete />}
                  data-test-id={PICK_CARRIERS_CARRIER_CONTACTS_MODAL_REMOVE_CC_BTN_TEST_ID}
                >
                  Remove
                </ButtonStyled>
              </CcFieldWrapperStyled>
            ))}
          </CcFieldsListStyled>
        </>
      )}
      {!areEmailsUnique && (
        <ErrorStyled data-test-id={PICK_CARRIERS_CARRIER_CONTACTS_MODAL_ERROR_TEST_ID}>
          <InfoButton width="2rem" height="2rem" color={colors.red[600]} />
          <span>Emails must be unique. Please remove duplicates before saving.</span>
        </ErrorStyled>
      )}
    </ModalContentStyled>
  );

  return (
    <FormModal
      show={showManageContactsModal}
      title="Manage Contacts"
      subtitle="Update your primary email and add a CC if needed."
      formContent={FormContent}
      submitButtonText="Save"
      onClose={closeManageContactsModal}
      onSubmit={saveContacts}
      icon={<Mail width="5.6rem" color={colors.green[600]} />}
      isFormValid={form.isFormValid() && areEmailsUnique}
      data-test-id={PICK_CARRIERS_CARRIER_ROW_CONTACTS_MODAL_TEST_ID}
    />
  );
});

export default ManageContactsModal;
